import { memo } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { PlantSource } from '../../types/plantSource';
import { usePlantSources } from '../../hooks/usePlantSourceQueries';
import { Button, ComboBox } from '../ui';
import type { ComboBoxOption } from '../ui';
import { useSearchInput } from '../../hooks/useSearchInput';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuth } from '../../hooks/useAuth';

interface PlantSourceSearchProps {
  value?: PlantSource | null;
  onChange: (source: PlantSource | null) => void;
  error?: string;
  placeholder?: string;
  onCreateNew?: () => void;
}

function PlantSourceSearchComponent({
  value,
  onChange,
  error,
  placeholder = 'Search for a plant source...',
  onCreateNew,
}: PlantSourceSearchProps) {
  const { user } = useAuth();

  const { searchValue: searchQuery, handleChange: handleSearchChange } = useSearchInput({
    onSearchChange: () => {}, // We handle the search in the useEffect below
    debounceMs: 300,
  });

  // Use debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch sources based on search - use 'all' scope for forms
  const { data, isLoading } = usePlantSources({
    search: debouncedSearchQuery.trim().length >= 2 ? debouncedSearchQuery : undefined,
    scope: 'all',
    limit: 20,
  });

  const sources = data?.sources || [];

  // Convert sources to ComboBox options
  // Always include the selected value in options, even if not in search results
  const allSources = [...sources];

  // Add the selected value if it's not already in the sources array
  if (value && !sources.some(s => s.id === value.id)) {
    allSources.unshift(value);
  }

  // Separate user's sources and other sources for grouping
  const userSources = user ? allSources.filter(s => s.createdBy === user.id) : [];
  const otherSources = user ? allSources.filter(s => s.createdBy !== user.id) : allSources;

  // Create grouped options with user's sources first
  const sourceOptions: ComboBoxOption<PlantSource>[] = [
    ...userSources.map(source => ({
      value: source,
      label: source.name,
      group: 'Your Sources',
    })),
    ...otherSources.map(source => ({
      value: source,
      label: source.name,
      group: 'All Sources',
    })),
  ];

  const renderSourceOption = (option: ComboBoxOption<PlantSource>) => {
    const source = option.value;
    return (
      <div className="flex flex-col">
        <span className="font-medium text-gray-900">{source.name}</span>
        {source.type && (
          <span className="text-sm text-gray-500 capitalize">
            {source.type.toLowerCase().replace('_', ' ')}
          </span>
        )}
        {source.website && <span className="text-xs text-gray-400">{source.website}</span>}
      </div>
    );
  };

  const dropdownFooter =
    searchQuery.trim().length >= 2 && onCreateNew ? (
      <div className="px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCreateNew}
          className="w-full justify-start text-green-600 hover:text-green-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add new source
        </Button>
      </div>
    ) : null;

  return (
    <ComboBox
      value={value}
      onChange={onChange}
      options={sourceOptions}
      placeholder={placeholder}
      error={error}
      searchable
      onSearch={handleSearchChange}
      searchQuery={searchQuery}
      loading={isLoading}
      emptyMessage={
        searchQuery.trim().length >= 2
          ? `No sources found for "${searchQuery}"`
          : 'Type at least 2 characters to search'
      }
      renderOption={renderSourceOption}
      dropdownFooter={dropdownFooter}
      getOptionKey={(source: PlantSource) => source.id}
    />
  );
}

export const PlantSourceSearch = memo(PlantSourceSearchComponent);
